/**
 * EncounterKnowledge — packChew ∩ charge corridor Soft foresight window.
 * Tag-based (PackChew / chargeCaster); sell as 통찰 encounter timing.
 * Soft≪AA: no combat coeff changes, Claim B banned.
 */
import type { RaidBoss, RaidUnit } from "../raid-types";
import {
  RaidEnemyTag,
  enemyHasTag,
  enemyIsCombatRelevant,
} from "../raid-enemy";
import {
  chargeCorridorActive,
  collectRaidDangerZones,
} from "../raid-threat";
import type { EncounterKnowledge } from "./hero-architecture";
import { getSoftAaIsolateFlags } from "./soft-aa-isolate";

export function packChargeOverlapOpen(
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  includeBoss = true,
): boolean {
  const zones = collectRaidDangerZones(boss, { enemies, includeBoss });
  if (!chargeCorridorActive(zones)) return false;
  return enemies.some(
    (e) =>
      enemyIsCombatRelevant(e) &&
      e.combatArmed &&
      enemyHasTag(e, RaidEnemyTag.PackChew),
  );
}

/** Soft≪AA foresight active when flag on and overlap window open. */
export function encounterPackChargeForesightActive(
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  includeBoss = true,
): boolean {
  if (!getSoftAaIsolateFlags().encounterPackChargeForesight) return false;
  return packChargeOverlapOpen(boss, enemies, includeBoss);
}

/**
 * Soft≪AA-safe encounter module: thrall+charge overlap → Soft foresight urgency.
 * Does not change combat coeffs — seat/survive Soft-clear uses urgency.
 */
export const packChargeOverlapKnowledge: EncounterKnowledge = {
  id: "pack_charge_overlap_window",
  enemyTags: ["packChew", "chargeCaster"],
  scenarioKinds: ["death", "pack_chew_enter"],
  windowOpen(args) {
    // Boss pose not in args — living PackChew alone is weak; callers use
    // packChargeOverlapOpen with boss. Here: any armed PackChew as proxy.
    return args.enemies.some(
      (e) =>
        enemyIsCombatRelevant(e) &&
        e.combatArmed &&
        enemyHasTag(e, RaidEnemyTag.PackChew),
    );
  },
  urgency01(args) {
    if (!packChargeOverlapKnowledge.windowOpen?.(args)) return 0;
    return 0.7;
  },
};
